/**
 * agb-color-aliases.js — locale-proof colour matching for the gear builder.
 * Dorian 2026-07-20.
 *
 * THE PROBLEM
 * On IL, variant option VALUES are translated: the same colour is 'Multicam' on
 * /en and 'מולטיקם' on the Hebrew site. But the colour logic is keyed by ENGLISH
 * names in two places:
 *   - COLOR_FALLBACK_MAP  (agb-store.js)   — the "requested colour is sold out,
 *                                            try these instead" chain
 *   - _mcTanRemap         (3 copies)       — the Multicam <-> Coyote Tan swap
 * so on Hebrew pages the lookup misses and BOTH features silently never fire.
 * Exact-colour matching still works (Hebrew base vs Hebrew options), which is
 * why this went unnoticed.
 *
 * Worse, the Hebrew catalogue is MIXED: a sizeable minority of products were
 * never translated, so a Hebrew page can serve 'Black' and 'שחור' side by side
 * (verified against the live catalogue: 347 variants, 'Black' x11 untranslated
 * alongside 'שחור' x79). Any fix therefore has to match ACROSS languages, not
 * merely swap one table for another.
 *
 * WHY A SEPARATE FILE
 * COLOR_FALLBACK_MAP lives in agb-store.js, which ships inside
 * agb-corepack.min.js — and there is NO builder for corepack in this repo, so
 * editing that source is unshippable. agb-variant.js captures the map BY
 * REFERENCE (`const colorFallbackMap = Store.COLOR_FALLBACK_MAP`), so mutating
 * the object in place at runtime is seen by the existing minified code. That
 * makes this file a pure additive layer: no upstream edits, no rebuild, and it
 * degrades to today's behaviour if anything is missing.
 *
 * SPELLINGS were derived empirically by fetching the live IL catalogue in both
 * locales and pairing variants BY ID, so these are the real strings in the data,
 * not a translation guess. Note 'חום' ("brown") is genuinely AMBIGUOUS — it is
 * used for both Coyote Tan and Coyote Brown — so it deliberately canonicalises
 * to BOTH, and matching is set-intersection rather than equality.
 */
(function () {
  'use strict';

  /* Canonical colour -> every spelling seen in the live catalogue.
     Keep the ENGLISH canonical name first in each list. */
  var SPELLINGS = {
    'Multicam':       ['Multicam', 'מולטיקם'],
    'Multicam Black': ['Multicam Black', 'מולטיקם שחור', 'שחור מולטיקם'],
    'Black':          ['Black', 'שחור'],
    'Ranger Green':   ['Ranger Green', "ירוק ריינג'ר", 'ירוק זית'],
    'Coyote Tan':     ['Coyote Tan', 'חום קויטי', 'מדברי', 'חום'],
    'Coyote Brown':   ['Coyote Brown', 'חום'],
    'M81 Woodland':   ['M81 Woodland'],
    'Wolf Grey':      ['Wolf Grey', 'אפור'],
    'Grey':           ['Grey', 'אפור']
  };

  /* Canonical fallback chains — mirrors COLOR_FALLBACK_MAP in agb-store.js.
     Kept in canonical terms; the spellings are expanded below. */
  var FALLBACKS = {
    'Multicam':       ['Coyote Tan', 'Coyote Brown', 'Ranger Green'],
    'Multicam Black': ['Black', 'Ranger Green'],
    'Ranger Green':   ['Multicam', 'Black', 'Coyote Tan'],
    'Black':          ['Multicam Black', 'Ranger Green'],
    'M81 Woodland':   ['Multicam', 'Ranger Green'],
    'Coyote Brown':   ['Coyote Tan', 'Multicam'],
    'Coyote Tan':     ['Coyote Brown', 'Multicam']
  };

  /* Normalise for lookup: trim, collapse inner whitespace, casefold. The live
     data really does contain 'שחור ' with a trailing space. */
  function norm(s) {
    return String(s == null ? '' : s).replace(/\s+/g, ' ').trim().toLowerCase();
  }

  /* spelling -> [canonical, ...]  (usually one; 'חום' yields two) */
  var CANON_BY_SPELLING = {};
  Object.keys(SPELLINGS).forEach(function (canon) {
    SPELLINGS[canon].forEach(function (sp) {
      var k = norm(sp);
      if (!CANON_BY_SPELLING[k]) CANON_BY_SPELLING[k] = [];
      if (CANON_BY_SPELLING[k].indexOf(canon) === -1) CANON_BY_SPELLING[k].push(canon);
    });
  });

  /* Canonical names for a raw option value. Unknown values return themselves,
     so non-colour options (sizes, 'Default Title') pass through untouched. */
  function canon(value) {
    var hit = CANON_BY_SPELLING[norm(value)];
    return hit ? hit.slice() : [String(value == null ? '' : value)];
  }

  /* Do two raw option values refer to the same colour? Set intersection, so the
     ambiguous 'חום' matches both Coyote Tan and Coyote Brown. */
  function same(a, b) {
    var ca = canon(a), cb = canon(b);
    for (var i = 0; i < ca.length; i++) if (cb.indexOf(ca[i]) !== -1) return true;
    return norm(a) === norm(b);
  }

  /* Build a fallback map keyed by EVERY spelling, whose values list every
     spelling of each fallback colour. The consumer iterates the array and takes
     the first available match, so listing all spellings means we never need to
     know which language a given product happens to use. */
  function buildFallbackMap() {
    var out = {};
    Object.keys(CANON_BY_SPELLING).forEach(function (spellKey) {
      var canons = CANON_BY_SPELLING[spellKey];
      var vals = [];
      canons.forEach(function (c) {
        // SAME COLOUR, OTHER LANGUAGE, FIRST. This is the fix for the base-colour
        // sync bug (Dorian 2026-07-21). The gear-builder's re-colour routine
        // (chooseVariantForColor, in the stale/unbuildable corepack) does a raw
        // `option1 === desiredColor` exact match, then walks THIS fallback list.
        // On a Hebrew page the base arrives as e.g. 'מולטיקם' while an untranslated
        // accessory variant is 'Multicam' — the exact match misses, so the ONLY
        // bridge is this list. If it led with substitute COLOURS (Coyote Tan…),
        // the accessory would re-colour to the wrong colour or get left on its
        // previous variant. Leading with the same colour's other spellings makes
        // the fallback loop land on the SAME colour across the language boundary
        // before ever considering a substitute. Still availability-gated by the
        // consumer, so a genuinely out-of-stock exact colour still falls through.
        (SPELLINGS[c] || []).forEach(function (sp) {
          if (norm(sp) !== spellKey && vals.indexOf(sp) === -1) vals.push(sp);
        });
        (FALLBACKS[c] || []).forEach(function (fb) {
          (SPELLINGS[fb] || [fb]).forEach(function (sp) {
            if (vals.indexOf(sp) === -1) vals.push(sp);
          });
        });
      });
      if (!vals.length) return;
      /* Re-key from the normalised form back to each original spelling, because
         the consumer looks up with the RAW option string. */
      canons.forEach(function (c) {
        (SPELLINGS[c] || []).forEach(function (sp) {
          if (norm(sp) === spellKey) out[sp] = vals.slice();
        });
      });
    });
    return out;
  }

  var GENERATED = buildFallbackMap();

  /* Public surface, consumed by the buildable copies of _mcTanRemap and by the
     easy-suggestion widget. */
  window.AGB_COLOR = {
    canon: canon,
    same: same,
    norm: norm,
    spellings: SPELLINGS,
    fallbackMap: GENERATED,
    /* Every known spelling of the same colour(s) as `value`, English first. */
    spellingsOf: function (value) {
      var out = [];
      canon(value).forEach(function (c) {
        (SPELLINGS[c] || []).forEach(function (sp) { if (out.indexOf(sp) === -1) out.push(sp); });
      });
      return out.length ? out : [String(value == null ? '' : value)];
    }
  };

  /* Merge into the live COLOR_FALLBACK_MAP. MUTATE IN PLACE — agb-variant.js
     holds a reference (`const colorFallbackMap = Store.COLOR_FALLBACK_MAP`), so
     reassigning would be invisible to it. Existing English keys are EXTENDED
     (their current values kept first, so today's priority order is preserved),
     never replaced. */
  function augment(store) {
    var map = store && store.COLOR_FALLBACK_MAP;
    if (!map || typeof map !== 'object') return false;
    Object.keys(GENERATED).forEach(function (key) {
      var existing = Array.isArray(map[key]) ? map[key].slice() : [];
      GENERATED[key].forEach(function (v) { if (existing.indexOf(v) === -1) existing.push(v); });
      map[key] = existing;
    });
    window.AGB_COLOR.augmented = true;
    return true;
  }

  /* The store module is created lazily by AGB.require. Poll briefly rather than
     hooking the boot sequence, so this file stays fully decoupled — if AGB never
     loads (a non-builder page) we simply do nothing. */
  var tries = 0;
  (function wait() {
    if (tries++ > 300) return;                        /* ~15s then give up quietly */
    try {
      if (window.AGB && typeof window.AGB.require === 'function') {
        var store = window.AGB.require('store');
        if (augment(store)) return;
      }
    } catch (e) { /* module not registered yet, or no store on this page */ }
    setTimeout(wait, 50);
  })();
})();
